/**
 * Collection Filters Handler
 * Handles AJAX filter updates for collection pages
 * Modular, reusable design
 */

class CollectionFilters {
  constructor() {
    this.debounceTimers = new Map();
    this.sectionId = null;
    this.isLoading = false;
    this.filters = new Map();
    this.availableValues = new Map();
    this.preservedSliderValues = new Map(); // Store slider values to preserve across AJAX updates

    // Filter labels for pills
    this.filterLabels = {
      price: 'Preis',
      module: 'Anzahl der Module',
      power: 'Leistung',
      capacity: 'Kapazität',
      expandability: 'Erweiterbarkeit',
      mit_speicher: 'Batteriespeicher'
    };

    // Filter configurations
    this.filterConfigs = {
      price: {
        selector: '[data-price-facet]',
        type: 'dual',
        initAttr: 'data-price-initialized',
        getURLParams: () => {
          const params = new URLSearchParams(window.location.search);
          return {
            min: params.get('filter.v.price.gte') ? parseFloat(params.get('filter.v.price.gte')) : null,
            max: params.get('filter.v.price.lte') ? parseFloat(params.get('filter.v.price.lte')) : null
          };
        }
      },
      module: {
        selector: '[data-module-facet]',
        type: 'single',
        initAttr: 'data-module-initialized',
        paramKey: 'anzahl_der_module',
        filterType: 'module',
        getURLParams: () => {
          const params = new URLSearchParams(window.location.search);
          return Array.from(params.entries())
            .filter(([key]) => key.includes('anzahl_der_module'))
            .map(([, value]) => parseInt(value) || 0);
        }
      },
      power: {
        selector: '[data-power-facet]',
        type: 'single',
        initAttr: 'data-power-initialized',
        paramKey: 'wattzahl|leistung',
        filterType: 'power',
        getURLParams: () => {
          const params = new URLSearchParams(window.location.search);
          return Array.from(params.entries())
            .filter(([key]) => key.includes('wattzahl') || key.includes('leistung'))
            .map(([, value]) => parseInt(value) || 0);
        }
      },
      capacity: {
        selector: '[data-capacity-facet]',
        type: 'single',
        initAttr: 'data-capacity-initialized',
        paramKey: 'kapazität|kapazitat',
        filterType: 'capacity',
        getURLParams: () => {
          const params = new URLSearchParams(window.location.search);
          return Array.from(params.entries())
            .filter(([key]) => key.includes('kapazität') || key.includes('kapazitat'))
            .map(([, value]) => parseFloat(value) || 0);
        }
      },
      expandability: {
        selector: '[data-expandability-facet]',
        type: 'single',
        initAttr: 'data-expandability-initialized',
        paramKey: 'erweiterbarkeit',
        filterType: 'expandability',
        getURLParams: () => {
          const params = new URLSearchParams(window.location.search);
          return Array.from(params.entries())
            .filter(([key]) => key.includes('erweiterbarkeit'))
            .map(([, value]) => parseFloat(value) || 0);
        }
      }
    };
  }

  init() {
    this.getSectionId();
    
    // Check if we're at /collections/all with no query params - reset everything to max values
    const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
    if (isCleanUrl) {
      // Clear any preserved slider values
      this.preservedSliderValues.clear();
    }
    
    Object.keys(this.filterConfigs).forEach(key => this.initFilters(key));
    this.initCheckboxFilters();
    this.initRadioFilters();
    this.initAccordionAnimations();
    this.setupPaginationHandlers();
    this.initSortHandlers();
    this.handleBrowserNavigation();
    this.updateFilterPills();
    this.setupRemoveAllButton();
    
    // Check initial URL state for dependent filters
    setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const mitSpeicherValue = params.get('filter.p.m.custom.mit_speicher');
      // Only show dependent filters when value is "with" (true/1), not "without" (false/0)
      const hasMitSpeicher = mitSpeicherValue === '1' || mitSpeicherValue === 'true' || mitSpeicherValue === true;
      this.toggleDependentFilters(hasMitSpeicher);
    }, 100);
  }

  getSectionId() {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      this.sectionId = productGrid.dataset.id;
    }
  }

  /**
   * Generic filter initialization
   */
  initFilters(filterKey) {
    const config = this.filterConfigs[filterKey];
    const containers = document.querySelectorAll(config.selector);
    
    // Check if we're at /collections/all with no query params - reset everything
    const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
    
    containers.forEach(container => {
      const existingData = this.filters.get(container);
      if (existingData && this.isElementReplaced(container, existingData, config)) {
        container.removeAttribute(config.initAttr);
        this.filters.delete(container);
      }
      
      if (!container.hasAttribute(config.initAttr)) {
        // Pass isCleanUrl flag if needed, but initDualSlider and initSingleSlider will check URL themselves
        config.type === 'dual' 
          ? this.initDualSlider(container, config)
          : this.initSingleSlider(container, config);
      }
    });
  }

  isElementReplaced(container, existingData, config) {
    if (config.type === 'dual') {
      const slider = container.querySelector('.price-facet__range--min');
      return existingData.minSlider !== slider;
    } else {
      // Determine slider class based on filter type
      let sliderClass;
      if (config.filterType === 'module') {
        sliderClass = '.module-facet__range';
      } else if (config.filterType === 'power') {
        sliderClass = '.power-facet__range';
      } else if (config.filterType === 'capacity') {
        sliderClass = '.capacity-facet__range';
      } else if (config.filterType === 'expandability') {
        sliderClass = '.expandability-facet__range';
      } else {
        // Fallback for backwards compatibility
        sliderClass = config.selector.includes('module') 
          ? '.module-facet__range' 
          : '.power-facet__range';
      }
      
      const slider = container.querySelector(sliderClass);
      return existingData.slider !== slider;
    }
  }

  /**
   * Initialize dual-thumb slider (price)
   */
  initDualSlider(container, config) {
    const minSlider = container.querySelector('.price-facet__range--min');
    const maxSlider = container.querySelector('.price-facet__range--max');
    const minDisplay = container.querySelector('[data-min-display]');
    const maxDisplay = container.querySelector('[data-max-display]');
    const sliderWrapper = container.querySelector('.price-facet__slider');
    const activeBar = container.querySelector('.price-facet__bar-active');

    if (!minSlider || !maxSlider || !sliderWrapper || !activeBar) return;

    const { min, max } = config.getURLParams();
    const minLimit = parseFloat(minSlider.min);
    const maxLimit = parseFloat(maxSlider.max);
    
    // Check if we're at /collections/all with no query params - reset to max values
    const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
    
    let minValue, maxValue;
    if (isCleanUrl || (min === null && max === null)) {
      // Reset to max values: min at minimum, max at maximum
      minValue = minLimit;
      maxValue = maxLimit;
    } else {
      minValue = min ?? parseFloat(minSlider.value) ?? minLimit;
      maxValue = max ?? parseFloat(maxSlider.value) ?? maxLimit;
    }
    
    const validated = this.validateRange(minValue, maxValue, minLimit, maxLimit);
    minSlider.value = validated.min;
    maxSlider.value = validated.max;

    this.updateDualDisplay(container, validated.min, validated.max);
    this.updateDualBar(sliderWrapper, activeBar, validated.min, validated.max, minLimit, maxLimit);

    const filterData = {
      type: 'dual',
      minSlider,
      maxSlider,
      minDisplay,
      maxDisplay,
      sliderWrapper,
      activeBar,
      minLimit,
      maxLimit,
      minValue: validated.min,
      maxValue: validated.max
    };

    this.filters.set(container, filterData);
    this.setupDualSliderEvents(container);
    container.setAttribute(config.initAttr, 'true');
  }

  /**
   * Initialize single-thumb slider (module, power)
   */
  initSingleSlider(container, config) {
    // Determine selectors based on filter type
    let sliderClass, inputClass, barClass, sliderElClass;
    
    if (config.filterType === 'module') {
      sliderClass = '.module-facet__range';
      inputClass = '[data-module-hidden-input]';
      barClass = '.module-facet__bar-active';
      sliderElClass = '.module-facet__slider';
    } else if (config.filterType === 'power') {
      sliderClass = '.power-facet__range';
      inputClass = '[data-power-hidden-input]';
      barClass = '.power-facet__bar-active';
      sliderElClass = '.power-facet__slider';
    } else if (config.filterType === 'capacity') {
      sliderClass = '.capacity-facet__range';
      inputClass = '[data-capacity-hidden-input]';
      barClass = '.capacity-facet__bar-active';
      sliderElClass = '.capacity-facet__slider';
    } else if (config.filterType === 'expandability') {
      sliderClass = '.expandability-facet__range';
      inputClass = '[data-expandability-hidden-input]';
      barClass = '.expandability-facet__bar-active';
      sliderElClass = '.expandability-facet__slider';
    } else {
      return; // Unknown filter type
    }
    
    const displayClass = '[data-value-display-text]';

    const slider = container.querySelector(sliderClass);
    const valueDisplay = container.querySelector(displayClass);
    const hiddenInput = container.querySelector(inputClass);
    const activeBar = container.querySelector(barClass);
    const sliderEl = container.querySelector(sliderElClass);

    if (!slider || !valueDisplay || !hiddenInput || !activeBar) return;

    let minValue = parseFloat(slider.min);
    const maxValue = parseFloat(slider.max);
    let step = parseFloat(slider.step);
    
    // For capacity: set min to 0 and step to 0.01 (to support values like 3.84)
    if (config.filterType === 'capacity') {
      minValue = 0;
      step = 0.01;
      slider.min = '0';
      slider.step = '0.01';
    }
    
    // Check if we have a preserved slider value from before AJAX update
    // Use filter type as key since there's only one capacity/expandability slider per type
    const preserveKey = config.filterType;
    let preservedValue = this.preservedSliderValues.get(preserveKey);
    
    // Get URL params early to check if we're initializing fresh
    const urlParams = config.getURLParams();
    const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
    const isFreshInit = preservedValue === undefined && urlParams.length === 0;
    
    let currentValue;
    if (isCleanUrl) {
      // If we're at /collections/all with no params, always reset to max value
      // Clear preserved value if it exists
      this.preservedSliderValues.delete(preserveKey);
      currentValue = maxValue;
    } else if (preservedValue !== undefined) {
      // Use preserved value if available (user's actual selection before AJAX)
      currentValue = preservedValue;
      // Clear the preserved value so it doesn't interfere with next update
      this.preservedSliderValues.delete(preserveKey);
    } else {
      // No preserved value, initialize from URL or DOM
      if (urlParams.length > 0) {
        currentValue = Math.max(...urlParams);
      } else {
        // Use maxValue directly for capacity/expandability when no URL params
        // This ensures the slider starts at the maximum available value
        currentValue = maxValue;
      }
    }
    
    currentValue = Math.max(minValue, Math.min(maxValue, currentValue));
    
    // For capacity: round to nearest 0.01 step; for expandability: round UP to next integer; for others: round to nearest step
    if (config.filterType === 'capacity') {
      // Round to 0.1 step and fix floating point precision
      // But if we're initializing from maxValue, keep it exact (don't round down)
      if (isFreshInit) {
        // Direct initialization from maxValue - use it exactly as-is (e.g., 3.84)
        currentValue = maxValue;
      } else {
        // Round to 0.01 step and fix floating point precision
        currentValue = Math.round(currentValue * 100) / 100;
        currentValue = Math.max(0, Math.min(maxValue, currentValue));
      }
    } else if (config.filterType === 'expandability') {
      currentValue = Math.ceil(currentValue);
    } else {
      currentValue = Math.round(currentValue / step) * step;
    }
    
    slider.value = currentValue;

    this.updateSingleDisplay(container, currentValue);
    this.updateSingleBar(sliderEl, activeBar, currentValue, minValue, maxValue);

    // Update minValue for capacity to be 0
    const effectiveMinValue = config.filterType === 'capacity' ? 0 : minValue;
    // Update step for capacity to be 0.01
    const effectiveStep = config.filterType === 'capacity' ? 0.01 : step;
    
    const filterData = {
      type: 'single',
      slider,
      valueDisplay,
      hiddenInput,
      activeBar,
      sliderEl,
      minValue: effectiveMinValue,
      maxValue,
      step: effectiveStep,
      currentValue,
      config
    };

    this.filters.set(container, filterData);
    this.setupSingleSliderEvents(container);
    container.setAttribute(config.initAttr, 'true');
  }

  /**
   * Setup events for dual slider
   */
  setupDualSliderEvents(container) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    this.removeEventListeners(filterData, ['minSlider', 'maxSlider']);

    const handlers = {
      minInput: () => this.handleDualInput(container, 'min'),
      maxInput: () => this.handleDualInput(container, 'max'),
      minChange: () => this.handleDualChange(container, 'min'),
      maxChange: () => this.handleDualChange(container, 'max')
    };

    filterData.listeners = handlers;
    filterData.minSlider.addEventListener('input', handlers.minInput);
    filterData.maxSlider.addEventListener('input', handlers.maxInput);
    filterData.minSlider.addEventListener('change', handlers.minChange);
    filterData.maxSlider.addEventListener('change', handlers.maxChange);
  }

  /**
   * Setup events for single slider
   */
  setupSingleSliderEvents(container) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    this.removeEventListeners(filterData, ['slider']);

    const handlers = {
      input: () => this.handleSingleInput(container),
      change: () => this.handleSingleChange(container)
    };

    filterData.listeners = handlers;
    filterData.slider.addEventListener('input', handlers.input);
    filterData.slider.addEventListener('change', handlers.change);
  }

  removeEventListeners(filterData, elements) {
    if (!filterData.listeners) return;
    
    elements.forEach(elem => {
      const slider = filterData[elem];
      if (slider) {
        ['input', 'change'].forEach(event => {
          const handlerKey = elem.includes('min') ? `min${event}` : elem.includes('max') ? `max${event}` : event;
          if (filterData.listeners[handlerKey]) {
            slider.removeEventListener(event, filterData.listeners[handlerKey]);
          }
        });
      }
    });
  }

  /**
   * Handle dual slider input
   */
  handleDualInput(container, type) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    const { minSlider, maxSlider, minLimit, maxLimit, sliderWrapper, activeBar } = filterData;
    let minValue = parseFloat(minSlider.value);
    let maxValue = parseFloat(maxSlider.value);

    if (type === 'min' && minValue >= maxValue) {
      minValue = Math.max(minLimit, maxValue - 1);
      minSlider.value = minValue;
    } else if (type === 'max' && maxValue <= minValue) {
      maxValue = Math.min(maxLimit, minValue + 1);
      maxSlider.value = maxValue;
    }

    filterData.minValue = minValue;
    filterData.maxValue = maxValue;
    this.updateDualDisplay(container, minValue, maxValue);
    this.updateDualBar(sliderWrapper, activeBar, minValue, maxValue, minLimit, maxLimit);
  }

  /**
   * Handle dual slider change
   */
  handleDualChange(container, type) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    const { minSlider, maxSlider, minLimit, maxLimit } = filterData;
    const validated = this.validateRange(
      parseFloat(minSlider.value),
      parseFloat(maxSlider.value),
      minLimit,
      maxLimit
    );

    minSlider.value = validated.min;
    maxSlider.value = validated.max;
    filterData.minValue = validated.min;
    filterData.maxValue = validated.max;
    this.updateDualDisplay(container, validated.min, validated.max);
    this.debounceFilterUpdate('price', () => this.applyPriceFilter(validated.min, validated.max));
  }

  /**
   * Handle single slider input
   */
  handleSingleInput(container) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    const { slider, minValue, maxValue, step, sliderEl, activeBar, config } = filterData;
    let newValue = parseFloat(slider.value);
    
    // For capacity: round to nearest 0.01 step; for expandability: round UP to next integer; for others: round to nearest step
    let roundedValue;
    if (config.filterType === 'capacity') {
      // Round to 0.1 step and fix floating point precision: multiply by 10, round, divide by 10
      roundedValue = Math.round(newValue * 100) / 100;
      roundedValue = Math.max(0, roundedValue);
    } else if (config.filterType === 'expandability') {
      roundedValue = Math.ceil(newValue);
    } else {
      roundedValue = Math.round(newValue / step) * step;
    }
    
    const currentValue = Math.max(minValue, Math.min(maxValue, roundedValue));

    slider.value = currentValue;
    filterData.currentValue = currentValue;
    this.updateSingleDisplay(container, currentValue);
    this.updateSingleBar(sliderEl, activeBar, currentValue, minValue, maxValue);
  }

  /**
   * Handle single slider change
   */
  handleSingleChange(container) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    const { slider, minValue, maxValue, step, config } = filterData;
    let newValue = parseFloat(slider.value);
    
    // For capacity: round to nearest 0.01 step; for expandability: round UP to next integer; for others: round to nearest step
    let roundedValue;
    if (config.filterType === 'capacity') {
      // Round to 0.1 step and fix floating point precision: multiply by 10, round, divide by 10
      roundedValue = Math.round(newValue * 100) / 100;
      roundedValue = Math.max(0, roundedValue);
    } else if (config.filterType === 'expandability') {
      roundedValue = Math.ceil(newValue);
    } else {
      roundedValue = Math.round(newValue / step) * step;
    }
    
    const currentValue = Math.max(minValue, Math.min(maxValue, roundedValue));
    
    slider.value = currentValue;
    filterData.currentValue = currentValue;
    this.updateSingleDisplay(container, currentValue);
    
    const filterKey = config.filterType;
    this.debounceFilterUpdate(filterKey, () => this.applySingleFilter(container, currentValue, filterData));
  }

  /**
   * Update dual display
   */
  updateDualDisplay(container, min, max) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US');
    if (filterData.minDisplay) filterData.minDisplay.textContent = formatter.format(Math.round(min));
    if (filterData.maxDisplay) filterData.maxDisplay.textContent = formatter.format(Math.round(max));
  }

  /**
   * Update single display
   */
  updateSingleDisplay(container, value) {
    const filterData = this.filters.get(container);
    if (!filterData) return;

    // Format value based on filter type to avoid floating point precision issues
    let displayValue;
    if (filterData.config.filterType === 'capacity') {
      // For capacity: format to 2 decimal places (e.g., 3.84)
      displayValue = parseFloat(value.toFixed(2));
    } else if (filterData.config.filterType === 'expandability') {
      // For expandability: format as integer
      displayValue = Math.ceil(value);
    } else {
      // For others (module, power): format as integer
      displayValue = Math.round(value);
    }

    if (filterData.valueDisplay) filterData.valueDisplay.textContent = displayValue;
    if (filterData.hiddenInput) filterData.hiddenInput.value = value;
  }

  /**
   * Update dual bar
   */
  updateDualBar(sliderWrapper, activeBar, min, max, minLimit, maxLimit) {
    const range = maxLimit - minLimit;
    if (range <= 0) return;

    const leftPercent = ((min - minLimit) / range) * 100;
    const widthPercent = ((max - min) / range) * 100;

    sliderWrapper.style.setProperty('--left', `${leftPercent}%`);
    sliderWrapper.style.setProperty('--width', `${widthPercent}%`);
    activeBar.style.left = `${leftPercent}%`;
    activeBar.style.width = `${widthPercent}%`;
  }

  /**
   * Update single bar
   */
  updateSingleBar(sliderEl, activeBar, value, minValue, maxValue) {
    const range = maxValue - minValue || 1;
    const position = ((value - minValue) / range) * 100;

    if (sliderEl) sliderEl.style.setProperty('--position', `${position}%`);
    if (activeBar) activeBar.style.width = `${position}%`;
  }

  /**
   * Validate range values
   */
  validateRange(min, max, minLimit, maxLimit) {
    min = Math.max(minLimit, Math.min(maxLimit, min));
    max = Math.max(minLimit, Math.min(maxLimit, max));

    if (min >= max) {
      min === minLimit ? (max = Math.min(maxLimit, min + 1)) : (min = Math.max(minLimit, max - 1));
    }

    return { min, max };
  }

  /**
   * Generic debounce
   */
  debounceFilterUpdate(key, fn) {
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key));
    }

    const timer = setTimeout(() => {
      fn();
      this.debounceTimers.delete(key);
    }, 250);

    this.debounceTimers.set(key, timer);
  }

  /**
   * Apply price filter
   */
  async applyPriceFilter(min, max) {
    if (this.isLoading) return;
    const params = this.updatePriceURLParams(min, max);
    await this.fetchProductGrid(params);
    this.updateFilterPills();
  }

  /**
   * Apply single slider filter
   */
  async applySingleFilter(container, currentValue, filterData) {
    if (this.isLoading) return;
    
    // Preserve the slider value before AJAX update (especially important for capacity/expandability)
    // Use filter type as key since there's only one capacity/expandability slider per type
    const preserveKey = filterData.config.filterType;
    this.preservedSliderValues.set(preserveKey, currentValue);
    
    const params = this.updateSingleSliderURLParams(currentValue, filterData);
    await this.fetchProductGrid(params);
    this.updateFilterPills();
  }

  /**
   * Update price URL params
   */
  updatePriceURLParams(min, max) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('filter.v.price.gte', Math.round(min).toString());
    params.set('filter.v.price.lte', Math.round(max).toString());
    this.updateBrowserURL(params);
    return params;
  }

  /**
   * Update single slider URL params
   */
  updateSingleSliderURLParams(currentValue, filterData) {
    const { minValue, maxValue, hiddenInput, config } = filterData;
    const paramName = hiddenInput.getAttribute('name');
    const params = new URLSearchParams(window.location.search);
    params.delete('page');

    // Remove existing filter params
    const keysToDelete = [];
    params.forEach((value, key) => {
      const paramKey = config.paramKey;
      const keyPattern = paramKey.includes('|') 
        ? paramKey.split('|').some(k => key.includes(k))
        : key.includes(paramKey);
      if (key === paramName || keyPattern) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => params.delete(key));

    // For capacity/expandability, always use exact values if available, even at max
    if (config.filterType === 'capacity' || config.filterType === 'expandability') {
      const availableValues = this.getAvailableFilterValues(config.filterType, paramName);
      
      if (availableValues.size > 0) {
        // Use only exact values from DOM that are <= currentValue (not rounded up)
        // For capacity: filter by exact currentValue; for expandability: use ceil since it's integer-based
        const maxFilterValue = config.filterType === 'capacity' 
          ? currentValue  // Use exact value for capacity (e.g., 3.6 means <= 3.6)
          : Math.ceil(currentValue);  // Use ceil for expandability (integer values)
        
        const matchingValues = Array.from(availableValues)
          .filter(val => val >= minValue && val <= maxFilterValue)
          .sort((a, b) => a - b);
        
        if (matchingValues.length > 0) {
          // Add all matching values
          matchingValues.forEach(val => {
            // Use exact decimal value (e.g., 3.84, not rounded)
            params.append(paramName, val.toString());
          });
        } else {
          // No matching values - slider is below minimum available value
          // Add an invalid value that won't match any products (to show no products)
          // Use a value that's definitely below all available values
          const minAvailableValue = Math.min(...Array.from(availableValues));
          // Use a value slightly below the minimum to ensure no matches
          // For capacity: use 0.01 step precision; for expandability: use integer
          if (config.filterType === 'capacity') {
            const belowMinValue = Math.max(0, minAvailableValue - 0.1);
            params.append(paramName, belowMinValue.toFixed(1));
          } else {
            const belowMinValue = Math.max(0, Math.floor(minAvailableValue) - 1);
            params.append(paramName, belowMinValue.toString());
          }
        }
      } else {
        // No available values yet - wait for first AJAX response
        // Don't add params, let Shopify return all products so we can extract values
      }
      // Update browser URL and return params
      this.updateBrowserURL(params);
      return params;
    }
    
    // For other filters (module, power), use exact value matching (not ranges)
    if (config.filterType === 'module' || config.filterType === 'power') {
      // For module and power: add ONLY the exact selected value (no ranges)
      // All previous values have already been cleared above (lines 682-692)
      if (currentValue >= minValue && currentValue <= maxValue) {
        // Round to integer for exact matching
        const exactValue = Math.round(currentValue);
        // Add only this single exact value - this will filter for products with exactly this value
        params.append(paramName, exactValue.toString());
      }
    } else {
      // For other filter types, keep existing "bis zu" logic (shouldn't reach here for single sliders)
      const isAtMax = currentValue >= maxValue;
      if (!isAtMax && currentValue >= minValue) {
        const availableValues = this.getAvailableFilterValues(config.filterType, paramName);
        
        if (availableValues.size > 0) {
          Array.from(availableValues)
            .filter(val => val >= minValue && val <= currentValue)
            .sort((a, b) => a - b)
            .forEach(val => params.append(paramName, val.toString()));
        }
      }
    }

    this.updateBrowserURL(params);
    return params;
  }

  /**
   * Update browser URL
   */
  updateBrowserURL(params) {
    const queryString = params.toString();
    const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}`;
    window.history.pushState({ searchParams: queryString }, '', newUrl);
  }

  /**
   * Get available filter values from URL
   */
  /**
   * Extract available filter values from DOM (filter checkboxes with exact values)
   */
  extractAvailableFilterValuesFromDOM(filterType, paramName) {
    const availableValues = new Set();
    
    if (filterType === 'capacity' || filterType === 'expandability') {
      // Method 1: Extract from data-filter-values attribute on the range element
      const selector = filterType === 'capacity' 
        ? 'capacity-range[data-capacity-range]' 
        : 'expandability-range[data-expandability-range]';
      
      const rangeElement = document.querySelector(selector);
      if (rangeElement) {
        const valuesStr = rangeElement.getAttribute('data-filter-values');
        if (valuesStr) {
          valuesStr.split(',').forEach(val => {
            const numValue = parseFloat(val.trim());
            if (!isNaN(numValue) && numValue > 0) {
              availableValues.add(numValue);
            }
          });
        }
      }
      
      // Method 2: Also try to find checkboxes (fallback)
      const paramKey = paramName.split('.').pop().split('_')[0];
      const allDependentContainers = document.querySelectorAll('.dependent-filter-nested');
      allDependentContainers.forEach(container => {
        const checkboxes = container.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
          const checkboxName = checkbox.getAttribute('name') || checkbox.getAttribute('data-filter-name') || '';
          if (checkboxName.includes(paramKey)) {
            const value = checkbox.getAttribute('value') || checkbox.getAttribute('data-filter-value');
            if (value) {
              const numValue = parseFloat(value);
              if (!isNaN(numValue) && numValue > 0) {
                availableValues.add(numValue);
              }
            }
          }
        });
      });
    }
    
    return availableValues;
  }

  /**
   * Get available filter values from DOM or URL
   */
  getAvailableFilterValues(filterType, paramName = null) {
    // Check stored values from HTML response first
    if ((filterType === 'capacity' || filterType === 'expandability') && this.availableValues.has(filterType)) {
      return this.availableValues.get(filterType);
    }
    
    // For capacity/expandability, try to extract exact values from DOM
    if (paramName && (filterType === 'capacity' || filterType === 'expandability')) {
      const domValues = this.extractAvailableFilterValuesFromDOM(filterType, paramName);
      if (domValues.size > 0) {
        return domValues;
      }
    }
    
    // Fallback to URL values
    const params = new URLSearchParams(window.location.search);
    const availableValues = new Set();
    const keyPatterns = {
      module: ['anzahl_der_module'],
      power: ['wattzahl', 'leistung'],
      capacity: ['kapazität', 'kapazitat'],
      expandability: ['erweiterbarkeit']
    };

    const patterns = keyPatterns[filterType] || [];
    params.forEach((value, key) => {
      if (patterns.some(pattern => key.includes(pattern))) {
        if (filterType === 'capacity' || filterType === 'expandability') {
          // Use exact decimal values
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && numValue > 0) availableValues.add(numValue);
        } else {
          const numValue = parseInt(value);
          if (!isNaN(numValue)) availableValues.add(numValue);
        }
      }
    });

    return availableValues;
  }

  /**
   * Fetch product grid via AJAX
   */
  async fetchProductGrid(params) {
    if (!this.sectionId) {
      this.getSectionId();
      if (!this.sectionId) {
        console.error('CollectionFilters: Section ID not found');
        return;
      }
    }

    this.isLoading = true;
    this.showLoadingState();

    try {
      const queryString = params.toString();
      const url = `${window.location.pathname}?sections=${this.sectionId}&${queryString}`;
      const response = await fetch(url);
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const sectionHTML = data[this.sectionId] 
        || data[`shopify-section-${this.sectionId}`]
        || Object.values(data)[0];

      if (sectionHTML) {
        this.updateProductGrid(sectionHTML);
      } else {
        throw new Error('Section HTML not found in response');
      }
    } catch (error) {
      console.error('CollectionFilters: Error fetching product grid', error);
      window.location.href = window.location.href;
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  /**
   * Extract available filter values from HTML response
   */
  extractAvailableValuesFromHTML(html, filterType, paramName) {
    const availableValues = new Set();
    
    if (filterType === 'capacity' || filterType === 'expandability') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find the facets wrapper to get filter structure
      const facetsWrapper = tempDiv.querySelector('#main-collection-filters, .facets-wrapper');
      if (!facetsWrapper) return availableValues;
      
      // Extract param key to search for
      const paramKey = paramName.split('.').pop().split('_')[0];
      
      // Method 1: Look for checkboxes (if they're rendered)
      const checkboxes = facetsWrapper.querySelectorAll(`input[type="checkbox"][name*="${paramKey}"], input[type="checkbox"][data-filter-name*="${paramKey}"]`);
      checkboxes.forEach(checkbox => {
        const value = checkbox.getAttribute('value') || checkbox.getAttribute('data-filter-value');
        if (value) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && numValue > 0) {
            availableValues.add(numValue);
          }
        }
      });
      
      // Method 2: Extract from nested filter container (where sliders are, but checkboxes might exist in HTML structure)
      const nestedContainers = facetsWrapper.querySelectorAll('.dependent-filter-nested');
      nestedContainers.forEach(container => {
        const containerCheckboxes = container.querySelectorAll('input[type="checkbox"]');
        containerCheckboxes.forEach(checkbox => {
          const checkboxName = checkbox.getAttribute('name') || checkbox.getAttribute('data-filter-name') || '';
          if (checkboxName.includes(paramKey)) {
            const value = checkbox.getAttribute('value') || checkbox.getAttribute('data-filter-value');
            if (value) {
              const numValue = parseFloat(value);
              if (!isNaN(numValue) && numValue > 0) {
                availableValues.add(numValue);
              }
            }
          }
        });
      });
      
      // Method 3: Extract from data-filter-values attribute on range elements in HTML response
      const selector = filterType === 'capacity' 
        ? 'capacity-range[data-capacity-range]' 
        : 'expandability-range[data-expandability-range]';
      
      const rangeElement = facetsWrapper.querySelector(selector);
      if (rangeElement) {
        const valuesStr = rangeElement.getAttribute('data-filter-values');
        if (valuesStr) {
          valuesStr.split(',').forEach(val => {
            const numValue = parseFloat(val.trim());
            if (!isNaN(numValue) && numValue > 0) {
              availableValues.add(numValue);
            }
          });
        }
      }
    }
    
    return availableValues;
  }

  /**
   * Update product grid with new HTML
   */
  updateProductGrid(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const newCollection = tempDiv.querySelector('.collection');
    const currentCollection = document.querySelector('.collection');

    if (!newCollection || !currentCollection) {
      console.error('CollectionFilters: Collection container not found');
      return;
    }

    // Extract available values for capacity/expandability before updating DOM
    // Always try to extract values for these filter types since they're nested
    const capacityValues = this.extractAvailableValuesFromHTML(html, 'capacity', 'filter.p.m.custom.kapazitat_speicher_nutzbar');
    if (capacityValues.size > 0) {
      this.availableValues.set('capacity', capacityValues);
    }
    
    const expandabilityValues = this.extractAvailableValuesFromHTML(html, 'expandability', 'filter.p.m.custom.erweiterbarkeit');
    if (expandabilityValues.size > 0) {
      this.availableValues.set('expandability', expandabilityValues);
    }

    currentCollection.innerHTML = newCollection.innerHTML;

    const newProductGrid = tempDiv.querySelector('#product-grid');
    const currentProductGrid = document.getElementById('product-grid');
    if (newProductGrid?.dataset.id && currentProductGrid) {
      this.sectionId = newProductGrid.dataset.id;
    }

    this.updateElement('#ProductCountDesktop', tempDiv);
    this.updateElement('#ProductCount', tempDiv);

    this.setupPaginationHandlers();

      setTimeout(() => {
        // Check if we're at /collections/all with no query params - reset everything
        const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
        if (isCleanUrl) {
          // Clear any preserved slider values
          this.preservedSliderValues.clear();
        }
        
        // Update dependent filters visibility first - wait a bit longer to ensure DOM is fully updated
        const params = new URLSearchParams(window.location.search);
        const mitSpeicherValue = params.get('filter.p.m.custom.mit_speicher');
        // Only show dependent filters when value is "with" (true/1), not "without" (false/0)
        const hasMitSpeicher = mitSpeicherValue === '1' || mitSpeicherValue === 'true' || mitSpeicherValue === true;
        this.toggleDependentFilters(hasMitSpeicher);
        
        // Then initialize all filters (including nested capacity/expandability)
        Object.keys(this.filterConfigs).forEach(key => this.initFilters(key));
        this.initCheckboxFilters();
        this.initRadioFilters();
        this.initAccordionAnimations();
        this.initSortHandlers();
        this.updateFilterPills();
        
        // Toggle again after a slight delay to catch any late-rendered elements
        setTimeout(() => {
          this.toggleDependentFilters(hasMitSpeicher);
        }, 50);
      }, 100);
  }

  updateElement(selector, tempDiv) {
    const newEl = tempDiv.querySelector(selector);
    const currentEl = document.querySelector(selector);
    if (newEl && currentEl) {
      currentEl.innerHTML = newEl.innerHTML;
    }
  }

  /**
   * Initialize sort handlers
   */
  initSortHandlers() {
    // Handle desktop sort dropdown
    const sortSelect = document.getElementById('SortBy');
    if (sortSelect && !sortSelect.hasAttribute('data-sort-initialized')) {
      sortSelect.addEventListener('change', (e) => {
        e.preventDefault();
        this.handleSortChange(e.target.value);
      });
      sortSelect.setAttribute('data-sort-initialized', 'true');
    }
    
    // Handle mobile sort dropdown
    const sortSelectMobile = document.getElementById('SortBy-mobile');
    if (sortSelectMobile && !sortSelectMobile.hasAttribute('data-sort-initialized')) {
      sortSelectMobile.addEventListener('change', (e) => {
        e.preventDefault();
        this.handleSortChange(e.target.value);
      });
      sortSelectMobile.setAttribute('data-sort-initialized', 'true');
    }
    
    // Prevent default form submission for sort forms
    const sortForms = document.querySelectorAll('#FacetSortForm, form[id*="FacetSortForm"]');
    sortForms.forEach(form => {
      if (!form.hasAttribute('data-sort-form-initialized')) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const select = form.querySelector('select[name="sort_by"]');
          if (select) {
            this.handleSortChange(select.value);
          }
        });
        form.setAttribute('data-sort-form-initialized', 'true');
      }
    });
  }

  /**
   * Handle sort change
   */
  async handleSortChange(sortValue) {
    if (this.isLoading) return;
    
    const params = new URLSearchParams(window.location.search);
    
    // Remove existing sort_by parameter
    params.delete('sort_by');
    
    // Add new sort_by parameter
    if (sortValue) {
      params.set('sort_by', sortValue);
    }
    
    // Reset to first page when sorting
    params.delete('page');
    
    this.updateBrowserURL(params);
    await this.fetchProductGrid(params);
    
    // Re-initialize sort handlers after DOM update (new selects may be rendered)
    setTimeout(() => {
      this.initSortHandlers();
    }, 100);
  }

  /**
   * Setup pagination handlers
   */
  setupPaginationHandlers() {
    document.querySelectorAll('.pagination__item[href]').forEach(link => {
      const newLink = link.cloneNode(true);
      link.parentNode.replaceChild(newLink, link);
      newLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePaginationClick(newLink.getAttribute('href'));
      });
    });
  }

  /**
   * Handle pagination click
   */
  async handlePaginationClick(href) {
    if (this.isLoading) return;

    const url = new URL(href, window.location.origin);
    const pageParam = url.searchParams.get('page');
    const currentUrl = new URL(window.location.href);
    
    pageParam ? currentUrl.searchParams.set('page', pageParam) : currentUrl.searchParams.delete('page');
    
    const params = new URLSearchParams(currentUrl.search);
    this.updateBrowserURL(params);
    await this.fetchProductGrid(params);
  }

  /**
   * Show/hide loading state
   */
  showLoadingState() {
    document.getElementById('product-grid')?.classList.add('loading');
    document.querySelector('.loading-overlay')?.classList.remove('hidden');
  }

  hideLoadingState() {
    document.getElementById('product-grid')?.classList.remove('loading');
    document.querySelector('.loading-overlay')?.classList.add('hidden');
  }

  /**
   * Initialize checkbox filters
   */
  initCheckboxFilters() {
    const checkboxes = document.querySelectorAll('[data-filter-type="checkbox"]');
    const params = new URLSearchParams(window.location.search);
    
    checkboxes.forEach(checkbox => {
      // Remove old listener if exists
      const existingData = this.filters.get(checkbox);
      if (existingData && existingData.handler) {
        checkbox.removeEventListener('change', existingData.handler);
      }
      
      // Get checkbox name and value
      const paramName = checkbox.getAttribute('name') || checkbox.getAttribute('data-filter-name');
      const checkboxValue = checkbox.getAttribute('value') || checkbox.getAttribute('data-filter-value');
      
      // Check if this checkbox value is in the URL parameters
      const isChecked = params.getAll(paramName).includes(checkboxValue);
      
      // Update checkbox checked state to match URL
      checkbox.checked = isChecked;
      
      // Update visual state - add/remove active class on parent label
      const label = checkbox.closest('label.facet-checkbox, label.facets__label');
      if (label) {
        if (isChecked) {
          label.classList.add('active');
        } else {
          label.classList.remove('active');
        }
      }
      
      // Store checkbox data
      const checkboxData = {
        name: paramName,
        value: checkboxValue,
        checked: isChecked
      };
      
      // Setup change event listener
      const handler = () => {
        this.handleCheckboxChange(checkbox);
      };
      
      checkboxData.handler = handler;
      checkbox.addEventListener('change', handler);
      
      // Store in filters map
      this.filters.set(checkbox, checkboxData);
    });
  }

  /**
   * Handle checkbox change
   */
  async handleCheckboxChange(checkbox) {
    if (this.isLoading) return;
    
    const checkboxData = this.filters.get(checkbox);
    if (!checkboxData) return;
    
    const { name, value } = checkboxData;
    const isChecked = checkbox.checked;
    
    // Update visual state immediately
    const label = checkbox.closest('label.facet-checkbox, label.facets__label');
    if (label) {
      if (isChecked) {
        label.classList.add('active');
      } else {
        label.classList.remove('active');
      }
    }
    
    // Debounce the update
    const debounceKey = `checkbox-${name}`;
    this.debounceFilterUpdate(debounceKey, () => {
      this.applyCheckboxFilter(checkbox, name, value, isChecked);
    });
  }

  /**
   * Apply checkbox filter
   */
  async applyCheckboxFilter(checkbox, paramName, value, isChecked) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    
    // Get all checkboxes with the same param name
    const allCheckboxes = document.querySelectorAll(`[data-filter-type="checkbox"][name="${paramName}"], [data-filter-type="checkbox"][data-filter-name="${paramName}"]`);

    // Synchronize matching checkboxes (e.g., desktop & mobile) to keep state aligned
    allCheckboxes.forEach(cb => {
      if (cb === checkbox) return;
      const cbValue = cb.getAttribute('value') || cb.getAttribute('data-filter-value');
      if (cbValue === value) {
        cb.checked = isChecked;
        const cbLabel = cb.closest('label.facet-checkbox, label.facets__label');
        if (cbLabel) {
          cbLabel.classList.toggle('active', isChecked);
        }
      }
    });
    
    // Remove all existing params for this filter
    const keysToDelete = [];
    params.forEach((val, key) => {
      if (key === paramName) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => params.delete(key));
    
    // Add all checked values
    const uniqueValues = new Set();
    allCheckboxes.forEach(cb => {
      if (cb.checked) {
        const cbValue = cb.getAttribute('value') || cb.getAttribute('data-filter-value');
        if (cbValue) {
          uniqueValues.add(cbValue);
        }
      }
    });
    uniqueValues.forEach(cbValue => params.append(paramName, cbValue));
    
    this.updateBrowserURL(params);
    await this.fetchProductGrid(params);
    this.updateFilterPills();
  }

  /**
   * Initialize radio button filters (Batteriespeicher anzeigen)
   */
  initRadioFilters() {
    const radios = document.querySelectorAll('[data-filter-type="radio"]');
    const params = new URLSearchParams(window.location.search);
    
    radios.forEach(radio => {
      const paramName = radio.getAttribute('data-filter-name');
      const radioValue = radio.getAttribute('value'); // "all", "with", or "without"
      const filterValue = radio.getAttribute('data-filter-value'); // "1" for "with", "false" or "0" for "without"
      const action = radio.getAttribute('data-filter-action'); // "remove" or "add"
      
      // Determine if this radio should be checked based on URL
      let isChecked = false;
      if (radioValue === 'all') {
        // "All" is checked if the filter param is NOT in URL
        isChecked = !params.has(paramName);
      } else if (radioValue === 'with' && filterValue) {
        // "With" is checked if the filter param value matches
        isChecked = params.getAll(paramName).includes(filterValue);
      } else if (radioValue === 'without' && filterValue) {
        // "Without" is checked if the filter param value matches
        isChecked = params.getAll(paramName).includes(filterValue);
      }
      
      // Update radio checked state (always update, even if already initialized)
      radio.checked = isChecked;
      
      // Always setup event listener (AJAX may replace DOM elements)
      // Remove old listener if exists
      const existingData = this.filters.get(radio);
      if (existingData && existingData.handler) {
        radio.removeEventListener('change', existingData.handler);
      }
      
      // Store radio data
      const radioData = {
        paramName,
        radioValue,
        filterValue,
        action,
        checked: isChecked
      };
      
      // Setup change event listener
      const handler = () => {
        this.handleRadioChange(radio);

      };
      
      radioData.handler = handler;
      radio.addEventListener('change', handler);
      
      // Store in filters map
      this.filters.set(radio, radioData);
      radio.setAttribute('data-radio-initialized', 'true');
    });
    
    // Set initial visibility of dependent filters based on current radio selection
    // Only show dependent filters when "with" is selected
    const withRadio = Array.from(radios).find(r => r.getAttribute('value') === 'with' && r.checked);
    this.toggleDependentFilters(!!withRadio);
  }

  /**
   * Handle radio button change
   */
  async handleRadioChange(radio) {
    if (this.isLoading) return;
    
    const radioData = this.filters.get(radio);
    if (!radioData) return;
    
    const { paramName, radioValue, filterValue, action } = radioData;
    
    // Debounce the update
    const debounceKey = `radio-${paramName}`;
    this.debounceFilterUpdate(debounceKey, () => {
      this.applyRadioFilter(paramName, radioValue, filterValue, action);
    });
  }

  /**
   * Apply radio filter
   */
  async applyRadioFilter(paramName, radioValue, filterValue, action) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    
    // Remove all existing params for this filter
    const keysToDelete = [];
    params.forEach((value, key) => {
      if (key === paramName || key.includes('mit_speicher')) {
        keysToDelete.push(key);
      }
      // Also remove capacity and expandability filters when switching to "all" or "without"
      if (radioValue === 'all' || radioValue === 'without' || action === 'remove') {
        if (key.includes('kapazität') || key.includes('kapazitat')) {
          keysToDelete.push(key);
        }
        if (key.includes('erweiterbarkeit')) {
          keysToDelete.push(key);
        }
        if (key.includes('speichermarke')) {
          keysToDelete.push(key);
        }
      }
    });
    keysToDelete.forEach(key => params.delete(key));
    
    // Add filter param if "with" or "without" is selected
    if ((radioValue === 'with' || radioValue === 'without') && filterValue && action === 'add') {
      params.append(paramName, filterValue);
    }
    // If "all" is selected (action === 'remove'), don't add any params (already removed above)
    
    // Do a full page refresh instead of AJAX for radio button changes
    const newURL = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.location.href = newURL;
  }

  /**
   * Toggle visibility of dependent filters (Kapazität and Erweiterbarkeit)
   */
  toggleDependentFilters(show) {
    // Find all dependent filter elements within the battery filter container
    let batteryContainer = document.querySelector('[data-filter-name="filter.p.m.custom.mit_speicher"]')?.closest('details');
    
    if (!batteryContainer) {
      // Try to find by ID pattern
      batteryContainer = document.querySelector('details[id*="mit_speicher"]');
    }
    
    if (batteryContainer) {
      // Find dependent filters nested inside the battery filter display
      const batteryDisplay = batteryContainer.querySelector('.facets__display, .facets__display-vertical');
      if (batteryDisplay) {
        const dependentFilters = batteryDisplay.querySelectorAll('.dependent-filter-nested');
        
        dependentFilters.forEach(filter => {
          if (show) {
            filter.style.display = '';
            filter.style.visibility = '';
            filter.style.opacity = '';
            filter.classList.remove('hidden');
            // Ensure parent containers are also visible
            let parent = filter.parentElement;
            while (parent && parent !== batteryDisplay) {
              if (parent.style) {
                parent.style.display = '';
              }
              parent.classList.remove('hidden');
              parent = parent.parentElement;
            }
          } else {
            filter.style.display = 'none';
            filter.classList.add('hidden');
          }
        });
      }
    }
    
    // Also search for capacity and expandability sliders/filters by their data attributes
    // These should be inside .dependent-filter-nested but let's be thorough
    const capacityFilters = document.querySelectorAll('[data-capacity-range], capacity-range');
    const expandabilityFilters = document.querySelectorAll('[data-expandability-range], expandability-range');
    
    [...capacityFilters, ...expandabilityFilters].forEach(filter => {
      const parentFilter = filter.closest('.dependent-filter-nested');
      if (parentFilter) {
        if (show) {
          parentFilter.style.display = '';
          parentFilter.style.visibility = '';
          parentFilter.style.opacity = '';
          parentFilter.classList.remove('hidden');
        } else {
          parentFilter.style.display = 'none';
          parentFilter.classList.add('hidden');
        }
      }
    });
    
    // Also handle filters with data-depends-on attribute
    const dependsOnFilters = document.querySelectorAll('[data-depends-on="mit_speicher"]');
    dependsOnFilters.forEach(filter => {
      if (show) {
        filter.style.display = '';
        filter.style.visibility = '';
        filter.style.opacity = '';
        filter.classList.remove('hidden');
      } else {
        filter.style.display = 'none';
        filter.classList.add('hidden');
      }
    });
    
    // Find and show/hide any details elements that are dependent filters (mobile)
    const allDetails = document.querySelectorAll('details.dependent-filter, details[id*="kapazitat"], details[id*="erweiterbarkeit"]');
    allDetails.forEach(detailsEl => {
      const summary = detailsEl.querySelector('summary');
      const isDependent = summary && (
        summary.textContent.includes('Batteriekapazität') ||
        summary.textContent.includes('Batterie Erweiterbarkeit') ||
        summary.textContent.includes('Kapazität') ||
        summary.textContent.includes('Erweiterbarkeit')
      );
      
      if (isDependent) {
        if (show) {
          detailsEl.style.display = '';
          detailsEl.style.visibility = '';
          detailsEl.style.opacity = '';
          detailsEl.classList.remove('hidden');
        } else {
          detailsEl.style.display = 'none';
          detailsEl.classList.add('hidden');
        }
      }
    });
  }

  /**
   * Initialize smooth accordion animations matching mega-grid smoothness
   */
  initAccordionAnimations() {
    const accordions = document.querySelectorAll('.facets__disclosure, .facets__disclosure-vertical');
    
    accordions.forEach(accordion => {
      // Skip if already initialized
      if (accordion.hasAttribute('data-accordion-animated')) return;
      
      const summary = accordion.querySelector('summary');
      const display = accordion.querySelector('.facets__display, .facets__display-vertical');
      
      if (!summary || !display) return;
      
      // Initialize state
      if (accordion.hasAttribute('open')) {
        display.style.height = 'auto';
        display.style.opacity = '1';
        display.style.transform = 'translateY(0) scale(1)';
      } else {
        display.style.height = '0px';
        display.style.opacity = '0';
        display.style.transform = 'translateY(10px) scale(0.98)';
      }
      
      const handleToggle = (e) => {
        const isOpening = accordion.hasAttribute('open');
        
        if (isOpening) {
          // Opening - get natural height first
          display.style.height = 'auto';
          const height = display.scrollHeight;
          display.style.height = '0px';
          
          // Force reflow
          void display.offsetHeight;
          
          // Animate open with mega-grid timing
          requestAnimationFrame(() => {
            display.style.transition = 'opacity 0.28s cubic-bezier(0.22, 0.61, 0.36, 1), transform 0.34s cubic-bezier(0.22, 0.61, 0.36, 1), height 0.34s cubic-bezier(0.22, 0.61, 0.36, 1)';
            display.style.height = `${height}px`;
            display.style.opacity = '1';
            display.style.transform = 'translateY(0) scale(1)';
          });
        } else {
          // Closing
          const height = display.scrollHeight;
          display.style.transition = 'opacity 0.2s cubic-bezier(0.22, 0.61, 0.36, 1), transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1), height 0.25s cubic-bezier(0.22, 0.61, 0.36, 1)';
          display.style.height = `${height}px`;
          
          void display.offsetHeight;
          
          requestAnimationFrame(() => {
            display.style.height = '0px';
            display.style.opacity = '0';
            display.style.transform = 'translateY(10px) scale(0.98)';
          });
        }
      };
      
      // Use toggle event for details element (fires after state change)
      accordion.addEventListener('toggle', handleToggle);
      
      accordion.setAttribute('data-accordion-animated', 'true');
    });
  }

  /**
   * Handle browser navigation
   */
  handleBrowserNavigation() {
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        // Check if we're at /collections/all with no query params - reset everything
        const isCleanUrl = window.location.pathname === '/collections/all' && window.location.search === '';
        if (isCleanUrl) {
          // Clear any preserved slider values
          this.preservedSliderValues.clear();
        }
        
        this.filters.clear();
        Object.keys(this.filterConfigs).forEach(key => this.initFilters(key));
        this.initCheckboxFilters();
        this.initRadioFilters();
        this.initAccordionAnimations();
        this.updateFilterPills();
      }, 100);
    });
  }

  /**
   * Get all active filters from URL
   */
  getActiveFilters() {
    const params = new URLSearchParams(window.location.search);
    const activeFilters = [];
    
    // Price filter
    const priceMin = params.get('filter.v.price.gte');
    const priceMax = params.get('filter.v.price.lte');
    if (priceMin || priceMax) {
      const min = priceMin ? parseFloat(priceMin) : null;
      const max = priceMax ? parseFloat(priceMax) : null;
      activeFilters.push({
        type: 'price',
        key: 'price',
        label: this.getPriceLabel(min, max),
        params: { min, max },
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          newParams.delete('filter.v.price.gte');
          newParams.delete('filter.v.price.lte');
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    }
    
    // Module filter
    const moduleParams = Array.from(params.entries())
      .filter(([key]) => key.includes('anzahl_der_module'))
      .map(([, value]) => parseInt(value));
    if (moduleParams.length > 0) {
      // With exact filtering, there should only be one value, but use max for safety
      const exactValue = Math.max(...moduleParams);
      activeFilters.push({
        type: 'module',
        key: 'module',
        label: `${exactValue} Module`,
        params: moduleParams,
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          newParams.forEach((value, key) => {
            if (key.includes('anzahl_der_module')) {
              newParams.delete(key);
            }
          });
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    }
    
    // Power filter
    const powerParams = Array.from(params.entries())
      .filter(([key]) => key.includes('wattzahl') || key.includes('leistung'))
      .map(([, value]) => parseInt(value));
    if (powerParams.length > 0) {
      // With exact filtering, there should only be one value, but use max for safety
      const exactValue = Math.max(...powerParams);
      activeFilters.push({
        type: 'power',
        key: 'power',
        label: `${exactValue} W`,
        params: powerParams,
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          newParams.forEach((value, key) => {
            if (key.includes('wattzahl') || key.includes('leistung')) {
              newParams.delete(key);
            }
          });
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    }
    
    // Capacity filter
    const capacityParams = Array.from(params.entries())
      .filter(([key]) => key.includes('kapazität') || key.includes('kapazitat'))
      .map(([, value]) => parseFloat(value));
    if (capacityParams.length > 0) {
      const maxValue = Math.max(...capacityParams);
      activeFilters.push({
        type: 'capacity',
        key: 'capacity',
        label: `bis zu ${maxValue} kWh`,
        params: capacityParams,
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          newParams.forEach((value, key) => {
            if (key.includes('kapazität') || key.includes('kapazitat')) {
              newParams.delete(key);
            }
          });
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    }
    
    // Expandability filter
    const expandabilityParams = Array.from(params.entries())
      .filter(([key]) => key.includes('erweiterbarkeit'))
      .map(([, value]) => parseFloat(value));
    if (expandabilityParams.length > 0) {
      const maxValue = Math.max(...expandabilityParams);
      activeFilters.push({
        type: 'expandability',
        key: 'expandability',
        label: `bis zu ${maxValue} kWh`,
        params: expandabilityParams,
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          newParams.forEach((value, key) => {
            if (key.includes('erweiterbarkeit')) {
              newParams.delete(key);
            }
          });
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    }
    
    // Battery storage radio filter
    if (params.has('filter.p.m.custom.mit_speicher')) {
      const filterValue = params.get('filter.p.m.custom.mit_speicher');
      const isWith = filterValue === '1' || filterValue === 'true' || filterValue === true;
      const isWithout = filterValue === '0' || filterValue === 'false' || filterValue === false;
      
      activeFilters.push({
        type: 'radio',
        key: 'mit_speicher',
        label: isWith ? 'Nur Produkte mit Speicher' : (isWithout ? 'Produkte ohne Speicher' : 'Nur Produkte mit Speicher'),
        params: [filterValue],
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          // Remove battery storage filter
          newParams.delete('filter.p.m.custom.mit_speicher');
          // Also remove capacity and expandability filters (dependent filters)
          newParams.forEach((value, key) => {
            if (key.includes('kapazität') || key.includes('kapazitat')) {
              newParams.delete(key);
            }
            if (key.includes('erweiterbarkeit')) {
              newParams.delete(key);
            }
          });
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
          // Hide dependent filters after AJAX update
          setTimeout(() => {
            this.toggleDependentFilters(false);
          }, 100);
        }
      });
    }
    
    // Checkbox filters (installation types, etc.)
    const checkboxGroups = new Map();
    params.forEach((value, key) => {
      if (key.includes('filter.p.m.custom') && !key.includes('mit_speicher') && 
          !key.includes('kapazität') && !key.includes('kapazitat') && 
          !key.includes('erweiterbarkeit') && !key.includes('anzahl_der_module') &&
          !key.includes('wattzahl') && !key.includes('leistung')) {
        const filterName = key.split('.').pop();
        if (!checkboxGroups.has(filterName)) {
          checkboxGroups.set(filterName, []);
        }
        checkboxGroups.get(filterName).push({ key, value });
      }
    });
    
    const filterLabelMap = {
      montageart: 'Montageart',
      speichermarke: 'Speichermarke'
    };
    
    checkboxGroups.forEach((items, filterName) => {
      const labelSet = new Set();
      items.forEach(item => {
        // Try to get label from checkbox in DOM
        const checkbox = document.querySelector(`[name="${item.key}"][value="${item.value}"]`);
        if (checkbox) {
          const labelEl = checkbox.closest('label');
          if (labelEl) {
            // Get the first text node or use the value directly
            // Clean up the label to remove product counts and extra whitespace
            const labelText = labelEl.textContent?.trim() || '';
            // Extract just the value - usually the first line or before any numbers
            const firstLine = labelText.split('\n')[0].trim();
            // Remove product count patterns like "(15 Produkte)" or "(15)"
            const cleaned = firstLine.replace(/\s*\([^)]*\)\s*$/, '').trim();
            if (cleaned) {
              labelSet.add(cleaned);
              return;
            }
          }
        }
        labelSet.add(item.value);
      });
      const labels = Array.from(labelSet);
      
      // Get filter label from summary - clean it up
      let filterLabel = filterLabelMap[filterName.toLowerCase()] || filterName;
      if (!filterLabelMap[filterName.toLowerCase()]) {
        const firstCheckbox = document.querySelector(`[name="${items[0].key}"]`);
        if (firstCheckbox) {
          const details = firstCheckbox.closest('details');
          if (details) {
            const summary = details.querySelector('summary');
            if (summary) {
              const summaryText = summary.textContent?.trim() || '';
              // Extract just the first line/word, removing extra text
              const summaryParts = summaryText.split('\n').map(s => s.trim()).filter(s => s);
              if (summaryParts.length > 0) {
                // Use the first meaningful part (skip empty strings)
                filterLabel = summaryParts[0];
              }
            }
          }
        }
      }
      
      activeFilters.push({
        type: 'checkbox',
        key: filterName,
        label: `${filterLabel}: ${labels.join(', ')}`,
        params: items,
        remove: () => {
          const newParams = new URLSearchParams(window.location.search);
          items.forEach(item => newParams.delete(item.key));
          newParams.delete('page');
          this.updateBrowserURL(newParams);
          this.fetchProductGrid(newParams);
        }
      });
    });
    
    return activeFilters;
  }

  /**
   * Get price label for pill
   */
  getPriceLabel(min, max) {
    if (min && max) {
      return `${this.formatPrice(min)} - ${this.formatPrice(max)}`;
    } else if (min) {
      return `ab ${this.formatPrice(min)}`;
    } else if (max) {
      return `bis ${this.formatPrice(max)}`;
    }
    return 'Preis';
  }

  /**
   * Format price
   */
  formatPrice(amount) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Update filter pills display
   */
  updateFilterPills() {
    const pillsContainer = document.getElementById('appliedFiltersList');
    const resetContainer = document.getElementById('resetButtonContainer');
    
    if (!pillsContainer || !resetContainer) return;
    
    const activeFilters = this.getActiveFilters();
    
    // Clear existing pills
    pillsContainer.innerHTML = '';
    
    // Create pills for each active filter
    activeFilters.forEach(filter => {
      const pill = document.createElement('div');
      pill.className = 'filter-pill box-border';
      pill.setAttribute('data-filter-type', filter.type);
      pill.setAttribute('data-filter-key', filter.key);
      
      const pillContent = document.createElement('span');
      pillContent.className = 'filter-pill__content';
      pillContent.textContent = filter.label;
      
      const pillRemove = document.createElement('button');
      pillRemove.className = 'filter-pill__remove';
      pillRemove.setAttribute('aria-label', 'Filter entfernen');
      pillRemove.type = 'button';
      pillRemove.innerHTML = this.getCloseIcon();
      pillRemove.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        filter.remove();
      });
      
      pill.appendChild(pillContent);
      pill.appendChild(pillRemove);
      pillsContainer.appendChild(pill);
    });
    
    // Show/hide reset button and container
    if (activeFilters.length > 0) {
      resetContainer.style.display = 'block';
      pillsContainer.closest('.applied-filters-display').style.display = 'block';
    } else {
      resetContainer.style.display = 'none';
      pillsContainer.closest('.applied-filters-display').style.display = 'none';
    }
  }

  /**
   * Get close icon SVG
   */
  getCloseIcon() {
    return `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;
  }

  /**
   * Setup remove all button
   */
  setupRemoveAllButton() {
    const removeAllBtn = document.querySelector('[data-filter-action="reset-all"]');
    if (!removeAllBtn) return;
    
    // Remove existing listeners by cloning
    const newBtn = removeAllBtn.cloneNode(true);
    removeAllBtn.parentNode.replaceChild(newBtn, removeAllBtn);
    
    newBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const params = new URLSearchParams(window.location.search);
      
      // Remove all filter parameters, keep only non-filter params
      const keysToDelete = [];
      params.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'page') {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => params.delete(key));
      
      this.updateBrowserURL(params);
      this.fetchProductGrid(params);
    });
  }
}

// Initialize on DOM ready
const collectionFilters = new CollectionFilters();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => collectionFilters.init());
} else {
  collectionFilters.init();
}

